import { ArkmeActionMenu } from './ArkmeDshMenu.js'
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ChangeEvent, type MouseEvent as ReactMouseEvent } from 'react'
import { createPortal } from 'react-dom'
import type {
  ArkmeFavoriteSticker, ArkmeFavoriteStickerAddInput, ArkmeFavoriteStickerList, ArkmeSourceSendResult, ArkmeUploadedAsset,
} from '../types.js'
import { callArkme } from './api.js'
import { arkmeRecentEmojiStore } from './emoji-recent-store.js'
import {
  arkmeDefaultEmojis, arkmeEmojiById, nextArkmeRecentEmojiIds,
  type ArkmeEmoji,
} from './arkme-emoji.js'
import { ArkmeComposerToolButton } from './ArkmeComposerToolButton.js'
import { ArkmeComposerEmojiIcon } from './ArkmeComposerToolIcon.js'
import type { ArkmeComposerCaretGeometry } from './ArkmeRichComposerInput.js'

const favoriteStickerLoadAttemptTimeoutMs = 6_000
const favoriteStickerLoadAttempts = 2
const favoriteStickerRetryDelayMs = 400

function waitForFavoriteStickerRetry(signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve()
  return new Promise(resolve => {
    const timer = setTimeout(done, favoriteStickerRetryDelayMs)
    function done() {
      clearTimeout(timer)
      signal.removeEventListener('abort', done)
      resolve()
    }
    signal.addEventListener('abort', done, { once: true })
  })
}

interface ArkmePendingFavoriteSticker {
  id: string
  file: File
  previewUrl: string
  status: 'uploading' | 'failed'
  error?: string
  uploadedAsset?: ArkmeUploadedAsset
}

interface ArkmeFavoriteStickerMenuState {
  x: number
  y: number
  stickerId: string
  kind: 'remote' | 'pending'
}

export interface ArkmeEmojiPanelGeometry {
  left: number
  top: number
  arrowCenterX: number
  placement: 'above' | 'below'
}

export function resolveArkmeEmojiPanelGeometry({ caret, editor, panelWidth, panelHeight, viewportWidth, viewportHeight }: {
  caret: ArkmeComposerCaretGeometry
  editor: ArkmeComposerCaretGeometry
  panelWidth: number
  panelHeight: number
  viewportWidth: number
  viewportHeight: number
}): ArkmeEmojiPanelGeometry {
  const margin = 12
  const arrowOffset = 10
  const maxLeft = Math.max(margin, viewportWidth - panelWidth - margin)
  const left = Math.max(margin, Math.min(editor.left, maxLeft))
  const availableAbove = editor.top - margin - arrowOffset
  const availableBelow = viewportHeight - editor.bottom - margin - arrowOffset
  const placement = availableAbove < panelHeight && availableBelow > availableAbove ? 'below' : 'above'
  const maxTop = Math.max(margin, viewportHeight - panelHeight - margin)
  const preferredTop = placement === 'above'
    ? editor.top - arrowOffset - panelHeight
    : editor.bottom + arrowOffset
  const top = Math.max(margin, Math.min(preferredTop, maxTop))
  const caretCenterX = (caret.left + caret.right) / 2
  const arrowCenterX = Math.max(18, Math.min(caretCenterX - left, panelWidth - 18))
  return { left, top, arrowCenterX, placement }
}

const styles: Record<string, CSSProperties> = {
  host: { position: 'relative', flex: 'none' },
  triggerIcon: { width: 20, height: 20, display: 'block', transform: 'translateY(1.5px)' },
  trigger: {
    width: 34, height: 34, flex: 'none', display: 'grid', placeItems: 'center', padding: 0, border: 0, borderRadius: 9,
    appearance: 'none', WebkitAppearance: 'none',
    background: 'transparent', color: 'var(--dsw-alias-label-secondary, #68707c)', cursor: 'pointer',
  },
  panelShell: { position: 'fixed', zIndex: 110, width: 'min(476px, calc(100vw - 24px))' },
  panel: {
    position: 'relative', width: '100%',
    maxHeight: 'min(368px, calc(100vh - 120px))', overflow: 'hidden', padding: 0,
    boxSizing: 'border-box', border: '1px solid var(--dsw-alias-border-l1, #e7e9ec)', borderRadius: 12,
    background: 'var(--dsw-specific-input-major, #fff)', boxShadow: '0 12px 34px rgba(20, 24, 31, .16)',
  },
  panelArrow: {
    position: 'absolute', zIndex: 1, width: 12, height: 12,
    transform: 'translateX(-50%) rotate(45deg)',
    background: 'var(--dsw-specific-input-major, #fff)',
    borderColor: 'var(--dsw-alias-border-l1, #e7e9ec)', borderStyle: 'solid',
    pointerEvents: 'none',
  },
  section: { margin: 0 },
  sectionSpaced: { margin: '18px 0 0' },
  titleRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 },
  title: { flex: 'none', color: 'var(--dsw-alias-label-secondary, #68707c)', fontSize: 12, lineHeight: '18px' },
  hint: { marginLeft: 'auto', color: 'var(--dsw-alias-label-tertiary, #9097a1)', fontSize: 11, lineHeight: '18px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 28px)', gap: 4 },
  defaultGrid: {
    // Fourteen columns at full panel width; wrap instead of hiding choices in narrow windows.
    gridTemplateColumns: 'repeat(auto-fit, 28px)',
    columnGap: 3, rowGap: 4, justifyContent: 'space-between',
  },
  emoji: {
    position: 'relative', width: 28, height: 28, display: 'grid', placeItems: 'center', padding: 0, border: 0, borderRadius: 6,
    background: 'transparent', cursor: 'pointer',
  },
  emojiImage: { width: 24, height: 24, display: 'block' },
  tooltip: {
    position: 'absolute', zIndex: 2, left: '50%', bottom: 32, transform: 'translateX(-50%)',
    padding: '4px 7px', borderRadius: 6, background: 'rgba(30, 30, 30, .92)', color: '#fff',
    boxShadow: '0 2px 8px rgba(0, 0, 0, .18)', fontSize: 12, lineHeight: '18px', whiteSpace: 'nowrap', pointerEvents: 'none',
  },
  tooltipArrow: {
    position: 'absolute', left: '50%', bottom: -4, width: 8, height: 8,
    transform: 'translateX(-50%) rotate(45deg)', background: 'rgba(30, 30, 30, .92)',
  },
  body: { maxHeight: 306, overflowY: 'auto', padding: '14px 16px 16px', boxSizing: 'border-box' },
  favoriteBody: { height: 278, overflowY: 'auto', padding: '16px 20px', boxSizing: 'border-box' },
  favoriteGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 82px)', gap: 8, alignContent: 'start' },
  stickerTile: {
    position: 'relative', width: 82, height: 82, padding: 0, overflow: 'hidden', border: 0, borderRadius: 12,
    background: 'var(--dsw-alias-fill-l2, #f5f6f7)', cursor: 'pointer',
  },
  stickerImage: { width: '100%', height: '100%', display: 'block', objectFit: 'cover' },
  stickerFallback: { width: '100%', height: '100%', display: 'grid', placeItems: 'center', color: '#a7adb5', fontSize: 24 },
  addTile: {
    width: 82, height: 82, display: 'grid', placeItems: 'center', padding: 0, borderRadius: 12,
    border: '1px dashed var(--dsw-alias-border-l2, #d9dde2)', background: 'transparent', color: '#c6c9ce', cursor: 'pointer',
  },
  gifBadge: {
    position: 'absolute', right: 6, bottom: 6, padding: '2px 5px', borderRadius: 7,
    background: 'rgba(32,32,32,.72)', color: '#fff', fontSize: 10, lineHeight: '15px',
  },
  empty: { gridColumn: '1 / -1', padding: '82px 0', textAlign: 'center', color: '#9aa0a8', fontSize: 13 },
  toolbar: {
    height: 48, display: 'flex', alignItems: 'center', gap: 8, padding: '0 20px', borderTop: '1px solid var(--dsw-alias-border-l1, #eceef0)',
  },
  tab: {
    width: 42, height: 40, display: 'grid', placeItems: 'center', padding: 0, border: 0, borderRadius: 10,
    background: 'transparent', color: '#a8adb4', cursor: 'pointer',
  },
  stateText: { padding: '92px 0', textAlign: 'center', color: '#8b929c', fontSize: 13 },
  retryButton: { marginTop: 8, padding: '4px 12px', border: 0, borderRadius: 7, background: 'transparent', color: '#596eab', cursor: 'pointer', fontSize: 13 },
  stickerOverlay: {
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: 8, boxSizing: 'border-box', borderRadius: 12, background: 'rgba(20,22,27,.55)', color: '#fff',
    fontSize: 11, lineHeight: '16px', pointerEvents: 'none',
  },
  spinner: { width: 13, height: 13, border: '2px solid rgba(255,255,255,.45)', borderTopColor: '#fff', borderRadius: 999 },
  skeleton: { width: 82, height: 82, borderRadius: 12, background: 'var(--dsw-alias-fill-l2, #f1f2f4)' },
}

function SmileIcon() {
  return <svg viewBox="0 0 24 24" width="21" height="21" fill="none" aria-hidden data-arkme-composer-action-icon="emoji">
    <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="9" cy="10" r="1" fill="currentColor" />
    <circle cx="15" cy="10" r="1" fill="currentColor" />
    <path d="M8.5 14c.9 1.25 2.05 1.88 3.5 1.88S14.6 15.25 15.5 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
}

function HeartIcon() {
  return <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
    <path d="M12 20.1S4 15.2 4 9.3C4 6.3 6.1 4.5 8.5 4.5c1.5 0 2.8.8 3.5 2 .7-1.2 2-2 3.5-2 2.4 0 4.5 1.8 4.5 4.8 0 5.9-8 10.8-8 10.8Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
  </svg>
}

function PlusIcon() {
  return <svg viewBox="0 0 24 24" width="28" height="28" fill="none" aria-hidden>
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
}

function RefreshIcon() {
  return <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden>
    <path d="M19 7v5h-5M5 17v-5h5M18.1 12a6.2 6.2 0 0 0-10.6-4.1L5 10m14 4-2.5 2.1A6.2 6.2 0 0 1 5.9 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
}

function StickerFallbackIcon() {
  return <svg viewBox="0 0 24 24" width="27" height="27" fill="none" aria-hidden>
    <rect x="4" y="5" width="16" height="14" rx="3" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="9" cy="10" r="1.3" fill="currentColor" />
    <path d="m6.5 17 4.2-4 2.7 2.4 2.1-1.8 2 1.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
}

function EmojiGrid({ emojis, layout = 'compact', onSelect }: {
  emojis: readonly ArkmeEmoji[]
  layout?: 'compact' | 'default'
  onSelect(emoji: ArkmeEmoji): void
}) {
  const [hoveredId, setHoveredId] = useState('')
  return <div
    data-arkme-emoji-grid={layout}
    style={{ ...styles.grid, ...(layout === 'default' ? styles.defaultGrid : {}) }}
  >
    {emojis.map(emoji => <button data-arkme-feedback="neutral"
      key={emoji.id}
      type="button"
      style={styles.emoji}
      aria-label={emoji.label}
      data-arkme-emoji-id={emoji.id}
      aria-describedby={hoveredId === emoji.id ? `arkme-emoji-tooltip-${emoji.id}` : undefined}
      onMouseDown={event => { event.preventDefault() }}
      onMouseEnter={() => { setHoveredId(emoji.id) }}
      onMouseLeave={() => { setHoveredId(current => current === emoji.id ? '' : current) }}
      onClick={() => { onSelect(emoji) }}
    ><img
      src={emoji.assetUrl}
      alt=""
      aria-hidden
      draggable={false}
      style={styles.emojiImage}
    />{hoveredId === emoji.id && <span id={`arkme-emoji-tooltip-${emoji.id}`} role="tooltip" style={styles.tooltip}>
      {emoji.label}<span aria-hidden style={styles.tooltipArrow} />
    </span>}</button>)}
  </div>
}

interface ArkmeEmojiPickerBaseProps {
  disabled: boolean
  accountKey?: string | undefined
  scopeKey: string | undefined
  getCaretGeometry?: () => ArkmeComposerCaretGeometry | undefined
  getEditorGeometry?: () => ArkmeComposerCaretGeometry | undefined
  onBeforeToggle?: () => void
  /** Return false when the editor rejects insertion; rejected choices are not recorded as recent. */
  onSelect(emoji: ArkmeEmoji): boolean | void
  onError?: (message: string) => void
}

/** Text insertion cannot accidentally acquire the private/group chat sticker-send contract. */
export type ArkmeEmojiPickerProps = ArkmeEmojiPickerBaseProps & ({
  mode: 'text'
  sourceRef?: never
  onUploadSticker?: never
  onStickerSent?: never
} | {
  mode?: 'all'
  sourceRef?: string
  onUploadSticker?: (file: File) => Promise<ArkmeUploadedAsset>
  onStickerSent?: (result: ArkmeSourceSendResult) => void | Promise<void>
})

export function ArkmeEmojiPicker({ disabled, mode = 'all', accountKey, scopeKey, sourceRef, getCaretGeometry, getEditorGeometry, onBeforeToggle, onSelect, onUploadSticker, onStickerSent, onError }: ArkmeEmojiPickerProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLElement>(null)
  const stickerInputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<'emoji' | 'favorite'>('emoji')
  const [stickers, setStickers] = useState<ArkmeFavoriteSticker[]>([])
  const [loadPhase, setLoadPhase] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [pendingStickers, setPendingStickers] = useState<ArkmePendingFavoriteSticker[]>([])
  const pendingStickersRef = useRef<ArkmePendingFavoriteSticker[]>([])
  const cancelledPendingStickerIdsRef = useRef<Set<string>>(new Set())
  const stickerLoadAbortRef = useRef<AbortController>()
  const disposedRef = useRef(false)
  const [busyStickerId, setBusyStickerId] = useState('')
  const [previewFailedIds, setPreviewFailedIds] = useState<Set<string>>(() => new Set())
  const [previewRevision, setPreviewRevision] = useState(0)
  const [contextMenu, setContextMenu] = useState<ArkmeFavoriteStickerMenuState>()
  const [recentIds, setRecentIds] = useState<string[]>([])
  const [recentSaveError, setRecentSaveError] = useState<string>()
  const [recentPhase, setRecentPhase] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const recentSelectionRevision = useRef(0)
  const recentReadRevision = useRef(0)
  const recentAccount = useRef(accountKey)
  recentAccount.current = accountKey
  const [panelGeometry, setPanelGeometry] = useState<ArkmeEmojiPanelGeometry>()
  const previousScopeKey = useRef(scopeKey)
  const recentEmojis = recentIds.map(id => arkmeEmojiById[id]).filter((emoji): emoji is ArkmeEmoji => emoji !== undefined)

  useEffect(() => {
    if (previousScopeKey.current !== scopeKey) setOpen(false)
    previousScopeKey.current = scopeKey
  }, [scopeKey])

  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  useEffect(() => {
    if (!open || typeof document === 'undefined') return
    const closeOnPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-arkme-action-menu][aria-label="收藏表情操作"]')) return
      if (event.target instanceof Node
        && hostRef.current?.contains(event.target) !== true
        && panelRef.current?.contains(event.target) !== true) setOpen(false)
      setContextMenu(undefined)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (contextMenu !== undefined) setContextMenu(undefined)
      else setOpen(false)
    }
    document.addEventListener('pointerdown', closeOnPointerDown)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnPointerDown)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open, contextMenu])

  const updatePanelGeometry = () => {
    if (!open || typeof window === 'undefined') return
    const hostRect = hostRef.current?.getBoundingClientRect()
    const fallbackGeometry = hostRect === undefined ? undefined : {
      left: hostRect.left, top: hostRect.top, right: hostRect.right, bottom: hostRect.bottom,
      width: hostRect.width, height: hostRect.height,
    }
    const caret = getCaretGeometry?.() ?? fallbackGeometry
    const editor = getEditorGeometry?.() ?? fallbackGeometry
    if (caret === undefined || editor === undefined) return
    const panelRect = panelRef.current?.getBoundingClientRect()
    const panelWidth = panelRect?.width || Math.min(476, Math.max(280, window.innerWidth - 24))
    const panelHeight = panelRect?.height || 368
    const next = resolveArkmeEmojiPanelGeometry({
      caret, editor, panelWidth, panelHeight,
      viewportWidth: window.innerWidth, viewportHeight: window.innerHeight,
    })
    setPanelGeometry(current => current !== undefined
      && current.left === next.left && current.top === next.top
      && current.arrowCenterX === next.arrowCenterX && current.placement === next.placement
      ? current : next)
  }

  useLayoutEffect(() => { updatePanelGeometry() })

  useEffect(() => {
    if (!open || typeof document === 'undefined') return
    const update = () => { requestAnimationFrame(updatePanelGeometry) }
    document.addEventListener('selectionchange', update)
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      document.removeEventListener('selectionchange', update)
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, getCaretGeometry, getEditorGeometry])

  useEffect(() => {
    pendingStickersRef.current = pendingStickers
  }, [pendingStickers])

  useEffect(() => {
    disposedRef.current = false
    return () => {
      disposedRef.current = true
      stickerLoadAbortRef.current?.abort()
      for (const item of pendingStickersRef.current) {
        cancelledPendingStickerIdsRef.current.add(item.id)
        if (item.previewUrl !== '') URL.revokeObjectURL(item.previewUrl)
      }
    }
  }, [])

  const loadRecent = async () => {
    if (accountKey === undefined) return
    const revision = recentSelectionRevision.current
    const readRevision = ++recentReadRevision.current
    const current = () => !disposedRef.current && recentAccount.current === accountKey
      && revision === recentSelectionRevision.current && readRevision === recentReadRevision.current
    setRecentPhase('loading')
    try {
      const ids = await arkmeRecentEmojiStore.recentEmojiIds(accountKey)
      if (!current()) return
      setRecentIds(ids)
      setRecentPhase('ready')
    } catch {
      if (current()) setRecentPhase('error')
    }
  }

  useEffect(() => {
    ++recentSelectionRevision.current
    setRecentIds([])
    setRecentSaveError(undefined)
    setRecentPhase('idle')
    setOpen(false)
  }, [accountKey])

  useEffect(() => {
    if (open) void loadRecent()
  }, [open, accountKey])

  const recordRecent = (emojiId: string) => {
    const revision = ++recentSelectionRevision.current
    setRecentSaveError(undefined)
    setRecentPhase('ready')
    if (accountKey === undefined) return
    void arkmeRecentEmojiStore.recordRecentEmoji(accountKey, emojiId).then(
      ids => {
        if (!disposedRef.current && recentAccount.current === accountKey && revision === recentSelectionRevision.current) setRecentIds(ids)
      },
      () => {
        if (!disposedRef.current && recentAccount.current === accountKey && revision === recentSelectionRevision.current) setRecentSaveError(emojiId)
      },
    )
  }

  const select = (emoji: ArkmeEmoji) => {
    if (disabled || onSelect(emoji) === false) return
    setRecentIds(current => nextArkmeRecentEmojiIds(current, emoji.id))
    recordRecent(emoji.id)
  }

  const loadStickers = async () => {
    stickerLoadAbortRef.current?.abort()
    const loadController = new AbortController()
    stickerLoadAbortRef.current = loadController
    setLoadPhase('loading')
    let lastError: unknown
    let lastAttemptTimedOut = false
    for (let attempt = 0; attempt < favoriteStickerLoadAttempts; attempt += 1) {
      const attemptController = new AbortController()
      const abortAttempt = () => attemptController.abort(loadController.signal.reason)
      loadController.signal.addEventListener('abort', abortAttempt, { once: true })
      let timeout: ReturnType<typeof setTimeout> | undefined
      let timedOut = false
      try {
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeout = setTimeout(() => {
            timedOut = true
            attemptController.abort()
            reject(new Error('收藏表情加载超时，请重试'))
          }, favoriteStickerLoadAttemptTimeoutMs)
        })
        const result = await Promise.race([
          callArkme<ArkmeFavoriteStickerList>('favorite-stickers.list', undefined, attemptController.signal),
          timeoutPromise,
        ])
        if (disposedRef.current || loadController.signal.aborted) {
          if (stickerLoadAbortRef.current === loadController) stickerLoadAbortRef.current = undefined
          return
        }
        setStickers(result.items)
        setPreviewFailedIds(new Set())
        setPreviewRevision(value => value + 1)
        setLoadPhase('success')
        if (stickerLoadAbortRef.current === loadController) stickerLoadAbortRef.current = undefined
        return
      } catch (caught) {
        if (disposedRef.current || loadController.signal.aborted) {
          if (stickerLoadAbortRef.current === loadController) stickerLoadAbortRef.current = undefined
          return
        }
        lastError = caught
        lastAttemptTimedOut = timedOut
      } finally {
        if (timeout !== undefined) clearTimeout(timeout)
        loadController.signal.removeEventListener('abort', abortAttempt)
      }
      if (attempt + 1 < favoriteStickerLoadAttempts) {
        await waitForFavoriteStickerRetry(loadController.signal)
        if (disposedRef.current || loadController.signal.aborted) {
          if (stickerLoadAbortRef.current === loadController) stickerLoadAbortRef.current = undefined
          return
        }
      }
    }
    if (!disposedRef.current && !loadController.signal.aborted) {
      const message = lastAttemptTimedOut
        ? '收藏表情加载超时，请重试'
        : lastError instanceof Error ? lastError.message : '收藏表情加载失败'
      setLoadPhase('error')
      onError?.(message)
    }
    if (stickerLoadAbortRef.current === loadController) stickerLoadAbortRef.current = undefined
  }

  useEffect(() => {
    if (mode === 'all' && open && tab === 'favorite' && loadPhase === 'idle') void loadStickers()
  }, [mode, open, tab, loadPhase])

  const addFavoriteSticker = async (item: ArkmeFavoriteStickerAddInput) => {
    const result = await callArkme<ArkmeFavoriteStickerList>('favorite-stickers.add', { item })
    setStickers(result.items)
    setLoadPhase('success')
  }

  const persistPendingSticker = async (pending: ArkmePendingFavoriteSticker) => {
    setBusyStickerId(pending.id)
    setPendingStickers(current => current.map(item => {
      if (item.id !== pending.id) return item
      const { error: _error, ...rest } = item
      return { ...rest, status: 'uploading' }
    }))
    try {
      const asset = pending.uploadedAsset ?? await onUploadSticker!(pending.file)
      if (disposedRef.current || cancelledPendingStickerIdsRef.current.has(pending.id)) return
      setPendingStickers(current => current.map(item => item.id === pending.id ? { ...item, uploadedAsset: asset } : item))
      await addFavoriteSticker({
        fileAssetUid: asset.fileAssetUid, fileName: asset.fileName, mimeType: asset.mimeType,
        size: asset.size, fileKind: 1, isAnimated: asset.mimeType.toLowerCase() === 'image/gif',
      })
      setPendingStickers(current => current.filter(item => item.id !== pending.id))
      if (pending.previewUrl !== '') URL.revokeObjectURL(pending.previewUrl)
    } catch (caught) {
      if (disposedRef.current || cancelledPendingStickerIdsRef.current.has(pending.id)) return
      const message = caught instanceof Error ? caught.message : '收藏表情添加失败'
      setPendingStickers(current => current.map(item => item.id === pending.id ? { ...item, status: 'failed', error: message } : item))
      onError?.(message)
    } finally {
      cancelledPendingStickerIdsRef.current.delete(pending.id)
      if (!disposedRef.current) setBusyStickerId(current => current === pending.id ? '' : current)
    }
  }

  const addSticker = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (file === undefined || onUploadSticker === undefined) return
    if (!file.type.toLowerCase().startsWith('image/')) { onError?.('请选择图片或 GIF'); return }
    const pending: ArkmePendingFavoriteSticker = {
      id: `pending:${crypto.randomUUID()}`, file,
      previewUrl: typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '',
      status: 'uploading',
    }
    setPendingStickers(current => [pending, ...current])
    void persistPendingSticker(pending)
  }

  const sendSticker = async (sticker: ArkmeFavoriteSticker) => {
    if (sourceRef === undefined || busyStickerId !== '' || !sticker.isAvailable || previewFailedIds.has(sticker.fileAssetUid)) return
    setBusyStickerId(sticker.fileAssetUid)
    try {
      const result = await callArkme<ArkmeSourceSendResult>('favorite-stickers.send', {
        sourceRef, fileAssetUid: sticker.fileAssetUid, recordUid: crypto.randomUUID(), relationUid: crypto.randomUUID(),
      })
      setOpen(false)
      await onStickerSent?.(result)
    } catch (caught) {
      onError?.(caught instanceof Error ? caught.message : '表情发送失败')
    } finally { setBusyStickerId('') }
  }

  const manageSticker = async (sticker: ArkmeFavoriteSticker, action: 'move-to-front' | 'delete') => {
    setContextMenu(undefined)
    setBusyStickerId(sticker.fileAssetUid)
    try {
      const result = await callArkme<ArkmeFavoriteStickerList>('favorite-stickers.manage', {
        fileAssetUid: sticker.fileAssetUid, action,
      })
      setStickers(result.items)
      setLoadPhase('success')
    } catch (caught) {
      onError?.(caught instanceof Error ? caught.message : '收藏表情管理失败')
    } finally { setBusyStickerId('') }
  }

  const removePendingSticker = (pending: ArkmePendingFavoriteSticker) => {
    setContextMenu(undefined)
    cancelledPendingStickerIdsRef.current.add(pending.id)
    setPendingStickers(current => current.filter(item => item.id !== pending.id))
    if (pending.previewUrl !== '') URL.revokeObjectURL(pending.previewUrl)
  }

  const retryPreview = (sticker: ArkmeFavoriteSticker, event?: ReactMouseEvent) => {
    event?.stopPropagation()
    setPreviewFailedIds(current => {
      const next = new Set(current)
      next.delete(sticker.fileAssetUid)
      return next
    })
    setPreviewRevision(value => value + 1)
    void loadStickers()
  }

  const showContextMenu = (event: ReactMouseEvent, stickerId: string, kind: 'remote' | 'pending') => {
    event.preventDefault()
    event.stopPropagation()
    const x = event.clientX
    const y = event.clientY
    setContextMenu({ x, y, stickerId, kind })
  }

  return <div ref={hostRef} style={styles.host} data-arkme-emoji-picker>
    <ArkmeComposerToolButton
      disabled={disabled}
      aria-label="选择表情"
      aria-haspopup="dialog"
      aria-expanded={open}
      data-arkme-composer-tool="emoji"
      onMouseDown={event => { onBeforeToggle?.(); event.preventDefault() }}
      onClick={() => { setOpen(value => !value) }}
    ><span style={styles.triggerIcon}><ArkmeComposerEmojiIcon /></span></ArkmeComposerToolButton>
    {open && (() => {
      const shell = <div
        style={{
          ...styles.panelShell,
          left: panelGeometry?.left ?? 0,
          top: panelGeometry?.top ?? 0,
          visibility: panelGeometry === undefined && typeof document !== 'undefined' ? 'hidden' : 'visible',
        }}
        data-arkme-emoji-panel-shell="true"
        data-placement={panelGeometry?.placement ?? 'above'}
      ><section ref={panelRef} role="dialog" aria-label="表情选择器" style={styles.panel} data-arkme-emoji-panel>
      {mode === 'text' || tab === 'emoji' ? <div style={styles.body}>
      {recentSaveError !== undefined && <div role="alert" style={styles.title}>最近表情保存未确认
        <button data-arkme-feedback="neutral" type="button" aria-label="重试保存最近表情" style={styles.retryButton} onClick={() => { recordRecent(recentSaveError) }}>重试保存</button>
      </div>}
      {recentPhase === 'loading'  && <div role="status" style={styles.title}>正在加载最近表情…</div>}
      {recentPhase === 'error' && <div role="alert" style={styles.title}>最近表情加载失败
        <button data-arkme-feedback="neutral" type="button" aria-label="重试加载最近表情" style={styles.retryButton} onClick={() => { void loadRecent() }}>重试</button>
      </div>}
      {recentEmojis.length > 0 && <div style={styles.section}>
        <div style={styles.titleRow}><span style={styles.title}>最近使用</span></div>
        <EmojiGrid emojis={recentEmojis} onSelect={select} />
      </div>}
      <div style={recentEmojis.length > 0 ? styles.sectionSpaced : styles.section}>
        <div style={styles.titleRow}>
          <span style={styles.title}>默认表情</span>
          <span style={styles.hint}>创作者：牛mo王</span>
        </div>
        <EmojiGrid emojis={arkmeDefaultEmojis} layout="default" onSelect={select} />
      </div></div> : <div style={styles.favoriteBody}>
        {loadPhase === 'error' && stickers.length === 0 && pendingStickers.length === 0 ? <div style={styles.stateText} role="alert">
          <div>加载失败</div>
          <button data-arkme-feedback="neutral" type="button" style={styles.retryButton} onClick={() => { void loadStickers() }}>重试</button>
        </div> : <div style={styles.favoriteGrid} data-arkme-favorite-sticker-grid="true">
          <button data-arkme-feedback="neutral"
            type="button" style={styles.addTile} aria-label="添加收藏表情" title="添加收藏表情"
            disabled={loadPhase === 'loading' || busyStickerId !== '' || onUploadSticker === undefined}
            onClick={() => { stickerInputRef.current?.click() }}
          ><PlusIcon /></button>
          {loadPhase === 'loading' && stickers.length === 0 && pendingStickers.length === 0 && Array.from({ length: 5 }, (_, index) => <div key={`skeleton:${String(index)}`} style={styles.skeleton} data-arkme-favorite-sticker-skeleton="true" />)}
          {pendingStickers.map(pending => <button
            key={pending.id} type="button" style={styles.stickerTile}
            aria-label={pending.status === 'failed' ? `重试${pending.file.name}` : `正在上传${pending.file.name}`}
            title={pending.error ?? pending.file.name}
            onClick={() => { if (pending.status === 'failed') void persistPendingSticker(pending) }}
            onContextMenu={event => { showContextMenu(event, pending.id, 'pending') }}
          >{pending.previewUrl !== '' ? <img src={pending.previewUrl} alt="" draggable={false} style={styles.stickerImage} /> : <span style={styles.stickerFallback}><StickerFallbackIcon /></span>}
            <span style={styles.stickerOverlay}>{pending.status === 'uploading' ? <><span style={styles.spinner} />上传中</> : <><RefreshIcon />点击重试</>}</span>
            {pending.file.type.toLowerCase() === 'image/gif' && <span style={styles.gifBadge}>GIF</span>}
          </button>)}
          {stickers.map(sticker => {
            const previewFailed = previewFailedIds.has(sticker.fileAssetUid)
            const sendSupported = sourceRef !== undefined
            const available = sendSupported && sticker.isAvailable && !previewFailed
            return <button
              key={sticker.fileAssetUid} type="button" style={{ ...styles.stickerTile, opacity: sticker.isAvailable ? 1 : .62 }}
              aria-label={available ? `发送${sticker.fileName}` : previewFailed ? `重试${sticker.fileName}` : !sendSupported ? `${sticker.fileName}仅聊天可发送` : `${sticker.fileName}不可用`}
              aria-disabled={!available}
              title={available ? sticker.fileName : previewFailed ? '预览加载失败，点击重试' : !sendSupported ? '收藏表情仅支持私聊和群聊发送' : sticker.unavailableReason}
              onClick={event => { if (previewFailed) retryPreview(sticker, event); else if (available) void sendSticker(sticker) }}
              onContextMenu={event => { showContextMenu(event, sticker.fileAssetUid, 'remote') }}
            >{sticker.mediaRef !== undefined && !previewFailed ? <img
                key={`${sticker.mediaRef}:${String(previewRevision)}`}
                src={`/arkme-self/api/media?ref=${encodeURIComponent(sticker.mediaRef)}&retry=${String(previewRevision)}`}
                alt="" draggable={false} style={styles.stickerImage}
                onError={() => { setPreviewFailedIds(current => new Set(current).add(sticker.fileAssetUid)) }}
              /> : <span style={styles.stickerFallback}><StickerFallbackIcon /></span>}
              {busyStickerId === sticker.fileAssetUid && <span style={styles.stickerOverlay}><span style={styles.spinner} />处理中</span>}
              {busyStickerId !== sticker.fileAssetUid && previewFailed && <span style={styles.stickerOverlay}><RefreshIcon />点击重试</span>}
              {busyStickerId !== sticker.fileAssetUid && !sticker.isAvailable && !previewFailed && <span style={styles.stickerOverlay}>不可用</span>}
              {sticker.isAnimated && <span style={styles.gifBadge}>GIF</span>}
            </button>
          })}
          {loadPhase !== 'loading' && stickers.length === 0 && pendingStickers.length === 0 && <div style={styles.empty}>暂无收藏表情，点击 + 添加</div>}
        </div>}
        <input ref={stickerInputRef} type="file" accept="image/*,.gif" hidden onChange={addSticker} />
      </div>}
      {mode === 'all' && <div style={styles.toolbar}>
        <button data-arkme-feedback="neutral" data-arkme-feedback-selected={tab === 'emoji'} type="button" style={{ ...styles.tab, ...(tab === 'emoji' ? { background: '#f3f4f5', color: '#737a84' } : {}) }} aria-label="默认表情" title="默认表情" onClick={() => { setTab('emoji') }}><SmileIcon /></button>
        <button data-arkme-feedback="neutral" data-arkme-feedback-selected={tab === 'favorite'} type="button" style={{ ...styles.tab, ...(tab === 'favorite' ? { background: '#f3f4f5', color: '#737a84' } : {}) }} aria-label="收藏表情" title="收藏表情" onClick={() => { setTab('favorite') }}><HeartIcon /></button>
      </div>}
      {mode === 'all' && contextMenu !== undefined && <ArkmeActionMenu label="收藏表情操作"
        point={{ x: contextMenu.x, y: contextMenu.y }} onClose={() => setContextMenu(undefined)}
        actions={contextMenu.kind === 'remote' ? (() => {
          const sticker = stickers.find(item => item.fileAssetUid === contextMenu.stickerId)
          if (!sticker) return []
          return [
            { id: 'front', label: '移至最前', disabled: busyStickerId !== '', onSelect: () => { void manageSticker(sticker, 'move-to-front') } },
            previewFailedIds.has(sticker.fileAssetUid) && { id: 'retry', label: '重试', onSelect: () => { setContextMenu(undefined); retryPreview(sticker) } },
            { id: 'delete', label: '删除', danger: true, disabled: busyStickerId !== '', onSelect: () => { void manageSticker(sticker, 'delete') } },
          ]
        })() : (() => {
          const pending = pendingStickers.find(item => item.id === contextMenu.stickerId)
          if (!pending) return []
          return [
            pending.status === 'failed' && { id: 'retry', label: '重试', onSelect: () => { setContextMenu(undefined); void persistPendingSticker(pending) } },
            { id: 'delete', label: '删除', danger: true, onSelect: () => removePendingSticker(pending) },
          ]
        })()} />}
      </section><span
        aria-hidden
        data-arkme-emoji-panel-arrow="true"
        style={{
          ...styles.panelArrow,
          left: panelGeometry?.arrowCenterX ?? 18,
          ...(panelGeometry?.placement === 'below'
            ? { top: -6, borderWidth: '1px 0 0 1px' }
            : { bottom: -6, borderWidth: '0 1px 1px 0' }),
        }}
      /></div>
      return typeof document === 'undefined' ? shell : createPortal(shell, document.body)
    })()}
  </div>
}
